import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  Card,
  CardContent,
  Grid as MuiGrid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
  ButtonGroup,
} from '@mui/material';
import {
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { 
  mockFundDetails, 
  mockLPs, 
  mockNAVAttributions, 
  mockCapitalEvents, 
  mockLPCommitments, 
  mockInvestmentAllocations,
  mockCashFlowCurve,
  CommitmentStatus,
} from '../utils/mockData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`fund-oversight-tabpanel-${index}`}
      aria-labelledby={`fund-oversight-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `fund-oversight-tab-${index}`,
    'aria-controls': `fund-oversight-tabpanel-${index}`,
  };
}

// Format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format percentage
const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

// Format multiplier
const formatMultiplier = (value: number): string => {
  return `${value.toFixed(2)}x`;
};

const FundOversight: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedFund, setSelectedFund] = useState<string>(mockFundDetails[1].id); // Default to Fund II
  const [navViewMode, setNavViewMode] = useState<'gross' | 'net'>('gross');
  const [lpFilterTags, setLpFilterTags] = useState<string[]>([]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFundChange = (event: React.ChangeEvent<{ value: unknown }> | any) => {
    setSelectedFund(event.target.value as string);
  };

  const handleNavViewModeChange = (mode: 'gross' | 'net') => {
    setNavViewMode(mode);
  };

  const handleLpFilterTagsChange = (event: React.ChangeEvent<{ value: unknown }> | any) => {
    setLpFilterTags(event.target.value as string[]);
  };

  // Get the selected fund data
  const currentFund = mockFundDetails.find(fund => fund.id === selectedFund) || mockFundDetails[0];
  
  // Get LPs for the selected fund
  const fundLPs = mockLPs.filter(lp => lp.fundId === selectedFund);
  
  // Get NAV attribution for the selected fund
  const fundNAVAttribution = mockNAVAttributions.find(nav => nav.fundId === selectedFund);
  
  // Get capital events for the selected fund
  const fundCapitalEvents = mockCapitalEvents
    .filter(event => event.fundId === selectedFund)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  
  // Get LP commitments for the selected fund
  const fundLPCommitments = mockLPCommitments.filter(commitment => commitment.fundId === selectedFund);
  
  // Get investment allocations for the selected fund
  const fundInvestmentAllocations = mockInvestmentAllocations.filter(allocation => allocation.fundId === selectedFund);
  
  // Get cash flow curve data for the selected fund
  const fundCashFlowData = mockCashFlowCurve.filter(point => point.fundId === selectedFund);

  // Calculate total commitments for LP Commitments tab
  const totalCommitments = fundLPCommitments.reduce((sum, commitment) => sum + commitment.amount, 0);
  const commitmentTarget = currentFund.totalCommitments;
  const commitmentProgress = (totalCommitments / commitmentTarget) * 100;

  // Filter LP commitments by tags if any are selected
  const filteredLPCommitments = lpFilterTags.length > 0
    ? fundLPCommitments.filter(commitment => 
        commitment.tags.some(tag => lpFilterTags.includes(tag))
      )
    : fundLPCommitments;

  // Get all unique tags from LP commitments
  const allLPTags = Array.from(
    new Set(
      fundLPCommitments.flatMap(commitment => commitment.tags)
    )
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ mb: 3, p: 2 }}>
        <Typography variant="h4">Fund Oversight</Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Track fund-level performance, LP cash flows, NAV progression, and investment allocations
        </Typography>
      </Paper>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="fund oversight tabs"
            variant="fullWidth"
          >
            <Tab label="Fund Dashboard" {...a11yProps(0)} />
            <Tab label="Capital Accounts" {...a11yProps(1)} />
            <Tab label="NAV Attribution" {...a11yProps(2)} />
            <Tab label="LP Commitments" {...a11yProps(3)} />
          </Tabs>
        </Box>

        {/* Fund Dashboard Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3 }}>
            <FormControl variant="outlined" sx={{ minWidth: 200 }}>
              <InputLabel id="fund-select-label">Select Fund</InputLabel>
              <Select
                labelId="fund-select-label"
                id="fund-select"
                value={selectedFund}
                onChange={handleFundChange}
                label="Select Fund"
              >
                {mockFundDetails.map((fund) => (
                  <MenuItem key={fund.id} value={fund.id}>
                    {fund.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Summary Metrics */}
          <MuiGrid container spacing={2} sx={{ mb: 4 }}>
            <MuiGrid sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 1.5 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Commitments
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {formatCurrency(currentFund.totalCommitments)}
                  </Typography>
                </CardContent>
              </Card>
            </MuiGrid>
            <MuiGrid sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 1.5 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Capital Called
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {formatCurrency(currentFund.capitalCalled)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {((currentFund.capitalCalled / currentFund.totalCommitments) * 100).toFixed(1)}% of commitments
                  </Typography>
                </CardContent>
              </Card>
            </MuiGrid>
            <MuiGrid sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 1.5 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Distributions
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {formatCurrency(currentFund.distributions)}
                  </Typography>
                </CardContent>
              </Card>
            </MuiGrid>
            <MuiGrid sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 1.5 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Residual NAV
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {formatCurrency(currentFund.residualNAV)}
                  </Typography>
                </CardContent>
              </Card>
            </MuiGrid>
          </MuiGrid>

          <MuiGrid container spacing={2} sx={{ mb: 4 }}>
            <MuiGrid sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 1.5 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    IRR
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {formatPercentage(currentFund.irr)}
                  </Typography>
                </CardContent>
              </Card>
            </MuiGrid>
            <MuiGrid sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 1.5 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    MOIC
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {formatMultiplier(currentFund.moic)}
                  </Typography>
                </CardContent>
              </Card>
            </MuiGrid>
            <MuiGrid sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 1.5 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    DPI
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {formatMultiplier(currentFund.dpi)}
                  </Typography>
                </CardContent>
              </Card>
            </MuiGrid>
            <MuiGrid sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, p: 1.5 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    TVPI
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 1 }}>
                    {formatMultiplier(currentFund.tvpi)}
                  </Typography>
                </CardContent>
              </Card>
            </MuiGrid>
          </MuiGrid>

          {/* Investment Allocation Chart */}
          <MuiGrid container spacing={3}>
            <MuiGrid sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Investment Allocation</Typography>
                    <IconButton size="small">
                      <PieChartIcon />
                    </IconButton>
                  </Box>
                  
                  {/* Simplified pie chart representation */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                    {fundInvestmentAllocations.map((allocation) => (
                      <Box key={allocation.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box 
                            sx={{ 
                              width: 16, 
                              height: 16, 
                              borderRadius: '50%', 
                              bgcolor: allocation.assetClass === 'Multifamily' ? '#4285F4' : 
                                      allocation.assetClass === 'Industrial' ? '#34A853' : 
                                      allocation.assetClass === 'Office' ? '#FBBC05' : 
                                      '#EA4335'
                            }} 
                          />
                          <Typography>{allocation.percentage}%</Typography>
                        </Box>
                        <Typography>{allocation.assetClass}</Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </MuiGrid>

            {/* Timeline of Capital Events */}
            <MuiGrid sx={{ width: { xs: '100%', md: '50%' }, p: 1.5 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Timeline of Capital Events</Typography>
                    <IconButton size="small">
                      <TimelineIcon />
                    </IconButton>
                  </Box>
                  
                  {/* Simplified timeline representation */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    {fundCapitalEvents.slice(0, 5).map((event, index) => (
                      <Box key={event.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          bgcolor: event.type === 'fund_launch' ? '#4285F4' : 
                                  event.type === 'capital_call' ? '#34A853' : 
                                  event.type === 'distribution' ? '#FBBC05' : 
                                  '#EA4335',
                          mt: 0.5
                        }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {event.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(event.date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(event.amount)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </MuiGrid>
          </MuiGrid>

          {/* Cash Flow Curve */}
          <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Cash Flow Curve</Typography>
                <IconButton size="small">
                  <TrendingUpIcon />
                </IconButton>
              </Box>
              
              {/* Simplified J-Curve representation */}
              <Box sx={{ height: 300, position: 'relative', mt: 2 }}>
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '1px', 
                  bgcolor: 'divider' 
                }} />
                
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 0, 
                  left: 0, 
                  height: '100%', 
                  width: '1px', 
                  bgcolor: 'divider' 
                }} />
                
                <Box sx={{ display: 'flex', height: '100%', alignItems: 'flex-end', pl: 4 }}>
                  {fundCashFlowData.map((point, index) => (
                    <Box 
                      key={point.id} 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        width: `${100 / fundCashFlowData.length}%`,
                        height: '100%',
                        position: 'relative'
                      }}
                    >
                      <Box sx={{ 
                        position: 'absolute',
                        bottom: `${(point.nav / (currentFund.totalCommitments * 0.8)) * 100}%`,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: '#4285F4',
                        zIndex: 3
                      }} />
                      
                      <Box sx={{ 
                        position: 'absolute',
                        bottom: `${(point.distributions / (currentFund.totalCommitments * 0.8)) * 100}%`,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: '#34A853',
                        zIndex: 2
                      }} />
                      
                      <Box sx={{ 
                        position: 'absolute',
                        bottom: `${(point.contributions / (currentFund.totalCommitments * 0.8)) * 100}%`,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: '#EA4335',
                        zIndex: 1
                      }} />
                      
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute', 
                          bottom: -24,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {point.year}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#EA4335' }} />
                  <Typography variant="body2">Contributions</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#34A853' }} />
                  <Typography variant="body2">Distributions</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4285F4' }} />
                  <Typography variant="body2">NAV</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Capital Accounts Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <FormControl variant="outlined" sx={{ minWidth: 200 }}>
              <InputLabel id="fund-select-label-cap-accounts">Select Fund</InputLabel>
              <Select
                labelId="fund-select-label-cap-accounts"
                id="fund-select-cap-accounts"
                value={selectedFund}
                onChange={handleFundChange}
                label="Select Fund"
              >
                {mockFundDetails.map((fund) => (
                  <MenuItem key={fund.id} value={fund.id}>
                    {fund.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />}
                sx={{ ml: 2 }}
              >
                Export to Excel
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />}
                sx={{ ml: 2 }}
              >
                Export to PDF
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>LP Name</TableCell>
                  <TableCell align="right">Commitment</TableCell>
                  <TableCell align="right">Contributions</TableCell>
                  <TableCell align="right">Distributions</TableCell>
                  <TableCell align="right">Unrealized NAV</TableCell>
                  <TableCell align="right">Preferred Return</TableCell>
                  <TableCell align="right">Carry %</TableCell>
                  <TableCell align="right">IRR</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundLPs.map((lp) => (
                  <TableRow key={lp.id}>
                    <TableCell>
                      <Typography variant="body2">{lp.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{lp.type.replace('_', ' ')}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(lp.commitment)}</TableCell>
                    <TableCell align="right">{formatCurrency(lp.contributions)}</TableCell>
                    <TableCell align="right">{formatCurrency(lp.distributions)}</TableCell>
                    <TableCell align="right">{formatCurrency(lp.unrealizedNAV)}</TableCell>
                    <TableCell align="right">{formatPercentage(lp.preferredReturn)}</TableCell>
                    <TableCell align="right">{formatPercentage(lp.carryPercentage)}</TableCell>
                    <TableCell align="right">{formatPercentage(lp.irr)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Detail">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* NAV Attribution Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 3 }}>
            <MuiGrid container spacing={2} alignItems="center">
              <MuiGrid sx={{ p: 1.5 }}>
                <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel id="fund-select-label-nav">Select Fund</InputLabel>
                  <Select
                    labelId="fund-select-label-nav"
                    id="fund-select-nav"
                    value={selectedFund}
                    onChange={handleFundChange}
                    label="Select Fund"
                  >
                    {mockFundDetails.map((fund) => (
                      <MenuItem key={fund.id} value={fund.id}>
                        {fund.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MuiGrid>
              <MuiGrid sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>View:</Typography>
                  <ButtonGroup size="small">
                    <Button 
                      variant={navViewMode === 'gross' ? 'contained' : 'outlined'} 
                      onClick={() => handleNavViewModeChange('gross')}
                    >
                      Gross
                    </Button>
                    <Button 
                      variant={navViewMode === 'net' ? 'contained' : 'outlined'} 
                      onClick={() => handleNavViewModeChange('net')}
                    >
                      Net of Fees
                    </Button>
                  </ButtonGroup>
                </Box>
              </MuiGrid>
              <MuiGrid sx={{ flexGrow: 1, p: 1.5 }} />
              <MuiGrid sx={{ p: 1.5 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<DownloadIcon />}
                >
                  Export
                </Button>
              </MuiGrid>
            </MuiGrid>
          </Box>

          {fundNAVAttribution ? (
            <>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>NAV Timeline</Typography>
                  <Typography variant="body1">
                    Total NAV as of {new Date(fundNAVAttribution.date).toLocaleDateString()}: {formatCurrency(fundNAVAttribution.totalNAV)}
                  </Typography>
                  
                  {/* Simplified NAV timeline chart */}
                  <Box sx={{ mt: 2, height: 200, position: 'relative' }}>
                    {/* This would be a real chart in a production app */}
                    <Box sx={{ 
                      position: 'absolute', 
                      bottom: 0, 
                      left: 0, 
                      width: '100%', 
                      height: '1px', 
                      bgcolor: 'divider' 
                    }} />
                    
                    <Box sx={{ 
                      position: 'absolute', 
                      bottom: 0, 
                      left: 0, 
                      height: '100%', 
                      width: '1px', 
                      bgcolor: 'divider' 
                    }} />
                    
                    <Typography variant="caption" sx={{ position: 'absolute', bottom: -20, left: 0 }}>
                      {new Date(currentFund.vintage, 0).getFullYear()}
                    </Typography>
                    
                    <Typography variant="caption" sx={{ position: 'absolute', bottom: -20, right: 0 }}>
                      {new Date().getFullYear()}
                    </Typography>
                    
                    <Box sx={{ 
                      position: 'absolute',
                      bottom: 0,
                      left: '10%',
                      width: '80%',
                      height: '70%',
                      borderTop: '2px solid #4285F4',
                      borderTopLeftRadius: 100,
                      borderTopRightRadius: 20,
                    }} />
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Attribution Breakdown</Typography>
                  
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Component</TableCell>
                          <TableCell align="right">Value</TableCell>
                          <TableCell align="right">% of Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fundNAVAttribution.components.map((component) => (
                          <TableRow key={component.id}>
                            <TableCell>
                              {component.name === 'noi' ? 'NOI' : 
                               component.name === 'capex' ? 'CapEx' : 
                               component.name === 'revaluation' ? 'Revaluation' : 
                               component.name === 'write_downs' ? 'Write-downs' : 
                               'Other'}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(component.value)}</TableCell>
                            <TableCell align="right">{formatPercentage(component.percentageOfTotal)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total NAV</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(fundNAVAttribution.totalNAV)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>100.0%</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Commentary</Typography>
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body1">
                    {fundNAVAttribution.commentary}
                  </Typography>
                </CardContent>
              </Card>
            </>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No NAV attribution data available for this fund
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* LP Commitments Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 3 }}>
            <MuiGrid container spacing={2} alignItems="center">
              <MuiGrid sx={{ p: 1.5 }}>
                <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel id="fund-select-label-lp">Select Fund</InputLabel>
                  <Select
                    labelId="fund-select-label-lp"
                    id="fund-select-lp"
                    value={selectedFund}
                    onChange={handleFundChange}
                    label="Select Fund"
                  >
                    {mockFundDetails.map((fund) => (
                      <MenuItem key={fund.id} value={fund.id}>
                        {fund.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MuiGrid>
              
              {allLPTags.length > 0 && (
                <MuiGrid sx={{ p: 1.5 }}>
                  <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                    <InputLabel id="lp-tags-label">Filter by Tags</InputLabel>
                    <Select
                      labelId="lp-tags-label"
                      id="lp-tags-select"
                      multiple
                      value={lpFilterTags}
                      onChange={handleLpFilterTagsChange}
                      label="Filter by Tags"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {allLPTags.map((tag) => (
                        <MenuItem key={tag} value={tag}>
                          {tag}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MuiGrid>
              )}
              
              <MuiGrid sx={{ flexGrow: 1, p: 1.5 }} />
              <MuiGrid sx={{ p: 1.5 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<DownloadIcon />}
                >
                  Export
                </Button>
              </MuiGrid>
            </MuiGrid>
          </Box>
          
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Commitment Progress</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1, mr: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={commitmentProgress} 
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Typography variant="body2">
                  {formatPercentage(commitmentProgress)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(totalCommitments)} of {formatCurrency(commitmentTarget)} target
              </Typography>
            </CardContent>
          </Card>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>LP Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Commitment</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLPCommitments.map((commitment) => {
                  const lp = mockLPs.find(l => l.id === commitment.lpId);
                  return (
                    <TableRow key={commitment.id}>
                      <TableCell>{lp?.name || 'Unknown LP'}</TableCell>
                      <TableCell>{lp?.type.replace('_', ' ') || 'Unknown'}</TableCell>
                      <TableCell align="right">{formatCurrency(commitment.amount)}</TableCell>
                      <TableCell>
                        <Chip 
                          size="small"
                          label={commitment.status.replace('_', ' ')}
                          color={
                            commitment.status === CommitmentStatus.Committed ? 'success' :
                            commitment.status === CommitmentStatus.InDiligence ? 'warning' :
                            commitment.status === CommitmentStatus.SoftCircled ? 'default' :
                            'error'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {commitment.tags.map(tag => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default FundOversight;
